/**
 * Manipulação de imagem no cliente por canvas nativo — sem biblioteca.
 *
 * Fonte única dos parâmetros de compressão do app: quem captura a foto
 * (FotosInput) e quem a edita depois (ImagemViewer) TÊM que usar os mesmos
 * números, senão cada giro/corte muda a qualidade e o tamanho do arquivo, e a
 * edição pode estourar o teto que a criação aceita (o backend valida os dois
 * caminhos com a mesma regra).
 *
 * Por que canvas nativo e não uma lib de crop: todas as fotos do módulo já
 * passam por canvas na captura (portanto já vêm sem EXIF e com no máximo
 * 1280px de lado), então girar/cortar é `drawImage` com 3 linhas de
 * transformação — não paga o peso de uma dependência nova no bundle.
 */

export const LADO_MAXIMO_PX = 1280;
export const QUALIDADE_JPEG = 0.72;

/**
 * Onde a foto REALMENTE fica dentro de uma caixa, espelhando o
 * `object-fit: scale-down` que o visualizador aplica.
 *
 * A imagem encolhe até caber e fica centralizada, sobrando tarja no eixo
 * folgado; se já cabe, aparece no tamanho natural — `scale-down` NÃO amplia
 * (é o que o diferencia de `contain`). Ampliar deixaria foto pequena borrada
 * e mudaria o comportamento que o visualizador já tinha.
 *
 * Quem precisa da foto — a janela de corte — não pode usar a caixa externa,
 * senão trata a tarja como parte da imagem e recorta a região errada. E o
 * corte grava sozinho, então o engano vai direto para o banco.
 *
 * @param {{width:number,height:number}} caixa  espaço disponível
 * @param {{width:number,height:number}} natural dimensões reais da foto
 * @returns {{left:number,top:number,width:number,height:number}} deslocamento
 *          relativo ao canto da caixa
 */
export function caixaDaFoto(caixa, natural) {
  const { width: cw, height: ch } = caixa || {};
  const { width: nw, height: nh } = natural || {};
  if (!cw || !ch || !nw || !nh) return { left: 0, top: 0, width: 0, height: 0 };
  const escala = Math.min(cw / nw, ch / nh, 1); // o `1` é o "não amplie"
  const width = nw * escala;
  const height = nh * escala;
  return { left: (cw - width) / 2, top: (ch - height) / 2, width, height };
}

export function carregarImagem(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    img.src = dataUrl;
  });
}

/**
 * Desenha `fonte` (img ou video) num canvas reduzido ao lado máximo e devolve
 * o JPEG. É o mesmo caminho que a captura de foto usa.
 */
export function comprimirFonte(fonte, larguraNatural, alturaNatural) {
  const escala = Math.min(
    1,
    LADO_MAXIMO_PX / Math.max(larguraNatural, alturaNatural),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(larguraNatural * escala));
  canvas.height = Math.max(1, Math.round(alturaNatural * escala));
  canvas.getContext("2d").drawImage(fonte, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", QUALIDADE_JPEG);
}

/**
 * Gira em múltiplos de 90°. Em 90/270 as dimensões trocam de lugar.
 *
 * Cada giro RE-CODIFICA o JPEG, então girar muitas vezes degrada a imagem —
 * por isso o app guarda o original no primeiro corte/giro e oferece
 * "Restaurar original".
 */
export async function girarDataUrl(dataUrl, graus) {
  const img = await carregarImagem(dataUrl);
  const quarto = ((Math.round(graus / 90) % 4) + 4) % 4; // normaliza p/ 0..3
  if (quarto === 0) return dataUrl;

  const trocaLados = quarto % 2 === 1;
  const largura = trocaLados ? img.height : img.width;
  const altura = trocaLados ? img.width : img.height;

  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext("2d");
  ctx.translate(largura / 2, altura / 2);
  ctx.rotate((quarto * 90 * Math.PI) / 180);
  ctx.drawImage(img, -img.width / 2, -img.height / 2);

  return comprimirFonte(canvas, canvas.width, canvas.height);
}

/**
 * Corta pela região informada em FRAÇÕES (0..1) do tamanho natural — assim o
 * chamador não precisa saber as dimensões reais nem lidar com a escala da
 * prévia na tela.
 */
export async function cortarDataUrl(dataUrl, { x, y, largura, altura }) {
  const img = await carregarImagem(dataUrl);
  const sx = Math.max(0, Math.round(x * img.width));
  const sy = Math.max(0, Math.round(y * img.height));
  const sw = Math.max(1, Math.round(largura * img.width));
  const sh = Math.max(1, Math.round(altura * img.height));

  const canvas = document.createElement("canvas");
  canvas.width = Math.min(sw, img.width - sx);
  canvas.height = Math.min(sh, img.height - sy);
  canvas
    .getContext("2d")
    .drawImage(img, sx, sy, canvas.width, canvas.height,
      0, 0, canvas.width, canvas.height);

  return comprimirFonte(canvas, canvas.width, canvas.height);
}

/** Data URL -> Blob, para download e Web Share (que exigem File/Blob). */
export function dataUrlParaBlob(dataUrl) {
  const [cabecalho, base64] = String(dataUrl).split(",");
  const mime = (cabecalho.match(/data:([^;]+)/) || [])[1] || "image/jpeg";
  const bin = atob(base64 || "");
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
